import type { FollowUpParticipantSummary } from '../../../../services/api/attendanceFollowUps';

export type GroupByMode = 'session' | 'group';

export interface NestedSubgroup {
  key: string;
  label: string;
  hasPending: boolean;
  items: FollowUpParticipantSummary[];
}

export interface NestedPrimaryGroup {
  key: string;
  label: string;
  pendingCount: number;
  sortKey: number;
  subgroups: NestedSubgroup[];
  allItems: FollowUpParticipantSummary[];
}

/**
 * Build the two-level nested grouping for the Level 1 participant list.
 *
 * mode='session':  Session (primary) → Group (subgroup) → participant rows
 * mode='group':    Group (primary)   → Session (subgroup) → participant rows
 *
 * Each participant is placed into a single primary/subgroup bucket based on
 * their primary_session and group affiliation. Primary groups carry a
 * pending-count pill; subgroups carry only an amber-dot "has pending"
 * indicator (per AC15-AC16a).
 */
export function nestedGroup(
  participants: FollowUpParticipantSummary[],
  mode: GroupByMode,
): NestedPrimaryGroup[] {
  const primaryMap = new Map<string, NestedPrimaryGroup>();

  for (const participant of participants) {
    const primary = mode === 'session' ? sessionKey(participant) : groupKey(participant);
    const sub = mode === 'session' ? groupKey(participant) : sessionKey(participant);

    let primaryGroup = primaryMap.get(primary.key);
    if (!primaryGroup) {
      primaryGroup = {
        key: primary.key,
        label: primary.label,
        pendingCount: 0,
        sortKey: primary.sortKey,
        subgroups: [],
        allItems: [],
      };
      primaryMap.set(primary.key, primaryGroup);
    }
    primaryGroup.pendingCount += participant.pending_count;
    primaryGroup.allItems.push(participant);

    let subgroup = primaryGroup.subgroups.find(s => s.key === sub.key);
    if (!subgroup) {
      subgroup = {
        key: sub.key,
        label: sub.label,
        hasPending: false,
        items: [],
      };
      primaryGroup.subgroups.push(subgroup);
    }
    subgroup.items.push(participant);
    if (participant.pending_count > 0) subgroup.hasPending = true;
  }

  // Sort primaries: session mode = newest-first by date; group mode = group name alpha.
  const primaries = Array.from(primaryMap.values());
  if (mode === 'session') {
    primaries.sort((a, b) => b.sortKey - a.sortKey);
  } else {
    primaries.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }

  // Sort subgroups within each primary: session mode = group alpha; group mode = session newest-first.
  for (const primary of primaries) {
    if (mode === 'session') {
      primary.subgroups.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    } else {
      // sub is session — sort newest-first based on any item's primary_session_date
      primary.subgroups.sort((a, b) => {
        const aDate = new Date(a.items[0]?.primary_session_date ?? 0).getTime();
        const bDate = new Date(b.items[0]?.primary_session_date ?? 0).getTime();
        return bDate - aDate;
      });
    }
    // Sort items within each subgroup by name for stable ordering.
    for (const sub of primary.subgroups) {
      sub.items.sort((a, b) => a.participant_name.localeCompare(b.participant_name));
    }
  }

  return primaries;
}

function sessionKey(p: FollowUpParticipantSummary): {
  key: string;
  label: string;
  sortKey: number;
} {
  return {
    key: `session:${p.primary_session_id}`,
    label: `Session ${p.primary_session_number} — ${formatSessionHeaderDate(p.primary_session_date)}`,
    sortKey: new Date(p.primary_session_date).getTime(),
  };
}

function groupKey(p: FollowUpParticipantSummary): {
  key: string;
  label: string;
  sortKey: number;
} {
  return {
    key: `group:${p.group_id ?? 'none'}`,
    label: p.group_name ?? 'No Group',
    sortKey: 0,
  };
}

function formatSessionHeaderDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'numeric',
    day: 'numeric',
  });
}
